<template>
    <div class="dynamic-content">
        <h1 class="page-title">XigFor SmartRewards™ Program - Terms & Conditions</h1>
        <p class="effective-date">Effective Date: September 10, 2025</p>

        <div class="content-section compact-section">
            <h2 class="section-title">1. Plan Overview</h2>
            <p class="desc-text">XigFor SmartRewards™ is designed for U.S. users to boost transaction activity and repurchase rates within regulatory compliance. The program operates on a voluntary basis (Opt-in/Opt-out) and prioritizes data transparency and risk management.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">2. Participation and Withdrawal</h2>
            <p class="desc-text bullet-text">Seller: When listing an item, you can choose to "Participate/Do Not Participate". By default, you will participate. You can modify the setting at any time without a cooldown period.</p>
            <p class="desc-text bullet-text">Buyer: After purchasing the product in the program, the corresponding reward task can be automatically activated after agreeing to the terms and conditions and authorization.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">3. Tasks and Qualified Orders</h2>
            <p class="desc-text bullet-text">When a buyer first meets the eligibility criteria for a purchase, the system will generate a reward mission for the buyer-seller pair.</p>
            <p class="desc-text bullet-text">Only verified orders that have not been refunded or canceled can be counted toward your task progress. Returns, cancellations, or abnormal transactions may result in adjusted or canceled rewards.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">4. Incentives for First Purchase and Repeat Purchase</h2>
            <p class="desc-text bullet-text">First purchase: The buyer gets a coupon for a percentage of the order amount, the seller gets merchant points, and the platform injects the reward pool proportionally for future incentives.</p>
            <p class="desc-text bullet-text">Follow-up purchases: When the buyer repurchases on the platform, they will receive coupons and achievement progress updates. The seller and any previous sellers associated with this buyer will earn merchant points according to the rules.</p>
            <p class="desc-text bullet-text">Progress tracking: View progress bar color and updates within the app (for growth stages, not a guarantee of earnings).</p>
            <p class="desc-text">Participation does not create any expectation of profit or future income.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">5. Credit Points, Coupons</h2>
            <p class="desc-text bullet-text">Credit Points are non-cash, non-transferable platform benefit credits.</p>
            <p class="desc-text bullet-text">Credit Points may be applied to eligible platform service costs designed to support participation, promotion, or content visibility within the platform ecosystem.</p>
            <p class="desc-text bullet-text">Examples of such services may include promotional tools or traffic-related platform services, subject to platform policies.</p>
            <p class="desc-text bullet-text">Credit Points cannot be redeemed for money, transferred, or used outside eligible platform services.</p>
            <p class="desc-text bullet-text">Buyer coupons: Valid only for products participating in the program. They expire automatically after the specified period.</p>
            <p class="desc-text">Credit Points are not redeemable for cash, have no cash value, and may only be used within the XigFor platform for eligible goods or services as specified from time to time.</p>
            <p class="desc-text">Certain redemption options may be introduced in the future, subject to eligibility requirements, regulatory approval, and separate terms.</p>
            <p class="desc-text bullet-text">The platform will periodically display applicable thresholds, validity periods, and availability scope within the application.</p>
            <p class="desc-text">Example calculation scenarios may be displayed for illustration only and do not represent guaranteed outcomes.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">6. Risk and Compliance Control</h2>
            <p class="desc-text bullet-text">Rewards for activities such as unusual refunds, fake orders, fraud, and abuse of coupons will be suspended, adjusted, or revoked to prevent arbitrage and abuse.</p>
            <p class="desc-text bullet-text">The platform may restrict participation eligibility for specific categories or high-risk merchants based on risk status.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">7. Budget and Adjustment</h2>
            <p class="desc-text bullet-text">The platform will set an annual or periodic budget cap for rewards and discounts. When the budget reaches the cap, the platform may adjust, reduce, or postpone the distribution with prior notice.</p>
            <p class="desc-text">The platform may adjust reward allocation methods, ratios, thresholds, or caps from time to time based on risk, budget, and regulatory considerations.</p>
            <p class="desc-text bullet-text">Legally awarded rewards that have been granted but not revoked are not retroactive.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">8. Privacy and Data Use</h2>
            <p class="desc-text bullet-text">
                This program complies with applicable U.S. federal and state privacy laws (including the CCPA). Scenarios involving data exchange in exchange for benefits will be disclosed as "financial incentives" in compliance with regulations and require user consent.
            </p>
            <p class="desc-text bullet-text">We do not sell personal information; user data is only used to identify, assign and verify rewards; users can withdraw their consent and exit the program at any time.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">9. Taxes and Disputes</h2>
            <p class="desc-text bullet-text">Tax obligations arising from reward redemption or in-site deductions shall be borne by the recipient.</p>
            <p class="desc-text bullet-text">Users may file a dispute through customer service within 15 business days. Disputes will be resolved through negotiation first, and if no agreement is reached, they will be arbitrated under the laws of Delaware.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">10. Clause Update and Effectiveness</h2>
            <p class="desc-text bullet-text">For major clause changes, the platform will notify users at least 30 days in advance via email, in-site notifications, or official website announcements. The version number and effective date will be recorded simultaneously.</p>
            <p class="desc-text bullet-text">These terms, together with the in-app display rules, constitute SmartRewards™ user agreement.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">Contact Information for SmartRewards Program</h2>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">Regulatory Clarification</h2>
            <p class="desc-text">SmartRewards™ is a promotional incentive program.</p>
            <p class="desc-text">It does not constitute an investment,</p>
            <p class="desc-text">revenue-sharing arrangement,</p>
            <p class="desc-text">commission program,</p>
            <p class="desc-text">or financial product.</p>
            <p class="desc-text">Rewards are discretionary,</p>
            <p class="desc-text">non-transferable,</p>
            <p class="desc-text">and issued solely</p>
            <p class="desc-text">to encourage platform engagement.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">Amendments</h2>
            <p class="desc-text">XigFor may update these Rules</p>
            <p class="desc-text">based on platform development, policy updates,</p>
            <p class="desc-text">or regulatory requirements.</p>
            <p class="desc-text">Continued participation after updates</p>
            <p class="desc-text">constitutes acceptance of the revised terms.</p>
        </div>

        <div class="content-section compact-section">
            <h2 class="section-title">Contact & Support</h2>
            <p class="desc-text">If you have any questions, concerns, complaints, or requests</p>
            <p class="desc-text">related to rewards, points, participation status,</p>
            <p class="desc-text">or these Terms & Conditions, please contact:</p>
            <p class="desc-text">Email: info@xigfor.com (Official Support Contact)</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SmartRewardsTerms'
}
</script>

<style scoped>
@import '../info/shared-info-styles.css';

.dynamic-content {
    width: 100%;
}

.page-title {
    margin-bottom: 6px;
    font-size: 40px;
    font-weight: 700;
    color: #393944;
}

.effective-date {
    font-size: 40px;
    font-weight: 700;
    color: #9B9CA5;
    margin-bottom: 16px;
    line-height: 1.5;
}

.compact-section {
    margin-bottom: 16px;
}

.section-title {
    font-family: 'Poppins Light', sans-serif;
    font-size: 26px;
    font-weight: 300;
    line-height: 56px;
    color: #6C6C78;
    margin-top: 0;
    margin-bottom: 8px;
}

.desc-text {
    font-family: 'Poppins Light', sans-serif;
    font-size: 26px;
    font-weight: 300;
    line-height: 56px;
    color: #6C6C78;
    margin-bottom: 2px;
}

.bullet-text {
    position: relative;
    padding-left: 12px;
}

.bullet-text::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.72em;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: currentColor;
}

@media (max-width: 900px) {
    .page-title {
        font-size: 20px;
        font-weight: 700;
        color: #393944;
        margin-bottom: 4px;
        line-height: 1.3;
    }

    .effective-date {
        font-size: 12px;
        font-weight: 700;
        color: #9B9CA5;
        margin-bottom: 10px;
    }

    .section-title {
        font-family: 'Poppins Light', sans-serif;
        font-size: 10px;
        font-weight: 300;
        line-height: 20px;
        color: #6C6C78;
        margin-bottom: 5px;
    }

    .desc-text {
        font-family: 'Poppins Light', sans-serif;
        font-size: 10px;
        font-weight: 300;
        line-height: 20px;
        color: #6C6C78;
        margin-bottom: 2px;
    }

    .compact-section {
        margin-bottom: 12px;
    }

    .bullet-text {
        padding-left: 10px;
    }

    .bullet-text::before {
        top: 0.7em;
        width: 3px;
        height: 3px;
    }
}
</style>
